import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import isLeapYear from 'dayjs/plugin/isLeapYear'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import updateLocale from 'dayjs/plugin/updateLocale'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import 'dayjs/locale/zh-cn'
import 'dayjs/locale/en'
import { loadStorage } from './cache'
import { DEFAULT_LOCALE_KEY, LOCALE_KEY } from '@/constant/cache-key'
import type { LocalEnum } from '@/types/enums'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isLeapYear)
// https://day.js.org/docs/zh-CN/display/format#list-of-localized-formats
dayjs.extend(localizedFormat)
dayjs.extend(updateLocale)
dayjs.extend(quarterOfYear)

const locale = loadStorage(LOCALE_KEY, DEFAULT_LOCALE_KEY)
const localeMap: Record<LocalEnum, 'zh-cn' | 'en'> = {
  'zh-CN': 'zh-cn',
  'en-US': 'en',
}
dayjs.locale(localeMap[locale])
// https://day.js.org/docs/zh-CN/customization/customization
dayjs.updateLocale('zh-cn', {
  formats: {
    l: 'YYYY-M-D',
    ll: 'YYYY-MM-D',
    lll: 'YYYY-MM-D h:mm A',
  },
})

// 时间格式化
export function formatTime(time: string | number, format = 'YYYY-MM-DD HH:mm:ss') {
  return time && dayjs(time).isValid() ? dayjs(time).format(format) : '-'
}

// 时间格式化，返回日期
export function formatLLTime(time: string | number, format = 'll') {
  return time && dayjs(time).isValid() ? dayjs(time).format(format) : '-'
}

// 转换为东八区时间
export function formatTimezone(time: string | number, format = 'YYYY-MM-DD HH:mm:ss') {
  return time && dayjs(time).isValid() ? dayjs(time).tz('Asia/Shanghai').format(format) : '-'
}

/**
 * 根据传入的时间字符串，判断已经过去几天了。
 * 时间字符串格式为：2021-08-01 12:00:00 或 2021-08-01 或 2021-08-01T12:00:00 或 2024-09-16T06:35:28.933Z 或 UTC 时间。
 * 使用 dayjs 进行处理。
 *
 * @param timeString - 时间字符串。
 * @returns 已经过去的天数。
 */
export function getDaysPassed(timeString: string): number {
  const currentTime = dayjs()
  const targetTime = dayjs(timeString)
  const diffInDays = currentTime.diff(targetTime, 'day')
  return diffInDays
}

// 获取当前时间的月份，支持传入时间，默认为当前时间
export function getMonth(time?: string | number) {
  return dayjs(time).isValid() ? dayjs(time).month() + 1 : 1
}

// 获取指定时间的年份
export function getYear(time?: string | number) {
  return dayjs(time).isValid() ? dayjs(time).year() : 2024
}

// 获取指定时间的季度
export function getQuarter(time?: string | number) {
  return dayjs(time).isValid() ? dayjs(time).quarter() : 1
}

export default dayjs
